"use client";

import { likeChapter, unlikeChapter } from "@/actions/chapter";
import { Button } from "@/components/ui/button";
import { TChapter, TChapterStats } from "@/types/types";
import { cn } from "@/utils/helpers";
import { useAuth } from "@clerk/nextjs";
import { captureException } from "@sentry/nextjs";
import { ThumbsUp } from "lucide-react";
import { useEffect, useState } from "react";
import Confetti from "react-dom-confetti";
import { toast } from "sonner";
type ChapterLikeButtonProps = {
  chapter: TChapter;
  chapterStats: TChapterStats;
};
export default function ChapterLikeButton({
  chapter,
  chapterStats
}: ChapterLikeButtonProps) {
  const [hasLiked, setHasLiked] = useState(chapterStats.hasLiked);
  const [likesCount, setLikesCount] = useState(chapterStats._count.chapterLikes);
  const [showConfetti, setShowConfetti] = useState(false);
  const {
    userId
  } = useAuth();
  const hasEnrolled = chapterStats.hasEnrolled;

  // clear state when going to next chapter
  useEffect(() => {
    setLikesCount(chapterStats._count.chapterLikes);
    setHasLiked(chapterStats.hasLiked);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function handleLikeToggle() {
    if (!userId) {
      toast.warning("Please sign in to like this chapter.");
      return;
    }
    if (!chapter.isFree && !hasEnrolled) {
      toast.error("Please purchase the course to give a like.");
      return;
    }
    try {
      if (hasLiked) {
        setHasLiked(false);
        setLikesCount(likesCount - 1);
        await unlikeChapter(chapter.id);
      } else {
        setHasLiked(true);
        setLikesCount(likesCount + 1);
        setShowConfetti(true);
        await likeChapter(chapter.id);
        setShowConfetti(false);
      }
    } catch (error) {
      console.error(error);
      captureException(error);
      setLikesCount(likesCount);
      setHasLiked(hasLiked);
      toast.error((error as Error).message);
    }
  }
  return <Button variant="outline" size="icon" className={cn("rounded-full", hasLiked ? "bg-courseAccent hover:brightness-90" : "hover:bg-muted")} onClick={() => handleLikeToggle()}>
      <ThumbsUp size={20} className={cn("opacity-90 transition", hasLiked && "text-darkGray")} />
      <Confetti active={showConfetti} />
    </Button>;
}